import { z } from "zod";

import adDensityMetricSchema from "./AdDensityMetricEvent";
import auctionPartnerSchema from "./AuctionPartnerEvent";
import coreWebVitalsThresholdSchema from "./CoreWebVitalsThresholdEvent";
import ecommerceSchema from "./Ecommerce";
import hawkSchema from "./HawkEvent";
import hybridAbTestSchema from "./HybridAbTest";
import membershipSchema from "./MembershipEvent";
import pageChangedSchema from "./PageChangedEvent";
import pageLoadSchema from "./PageLoadEvent";
import productLikedSchema from "./ProductLikedEvent";
import productUnlikedSchema from "./ProductUnlikedEvent";
import sourcepointConsentCapturedPayloadSchema from "./SourcepointConsentCapturedEvent";
import userSchema from "./User";

const eventSchemaGenerator = <T extends z.ZodTypeAny>(
  schema: T,
  name: string
) => {
  return z
    .object({ payload: schema })
    .merge(z.object({ name: z.literal(name) }));
};

// Zod Schemas
export const hawkEventSchema = eventSchemaGenerator(hawkSchema, "hawkEvent");
export const pageLoadEventSchema = eventSchemaGenerator(
  pageLoadSchema,
  "pageLoadEvent"
);
export const pageChangedEventSchema = eventSchemaGenerator(
  pageChangedSchema,
  "pageChangedEvent"
);
export const auctionPartnerEventSchema = eventSchemaGenerator(
  auctionPartnerSchema,
  "auctionPartnerEvent"
);
export const userEventSchema = eventSchemaGenerator(userSchema, "userIdEvent");
export const hybridAbTestEventSchema = eventSchemaGenerator(
  hybridAbTestSchema,
  "hybridAbTestEvent"
);
export const ecommerceEventSchema = eventSchemaGenerator(
  ecommerceSchema,
  "ecommerceEvent"
);
export const productLikedEventSchema = eventSchemaGenerator(
  productLikedSchema,
  "productLikedEvent"
);
export const productUnlikedEventSchema = eventSchemaGenerator(
  productUnlikedSchema,
  "productUnlikedEvent"
);
export const coreWebVitalsThresholdEventSchema = eventSchemaGenerator(
  coreWebVitalsThresholdSchema,
  "coreWebVitalsThresholdEvent"
);
export const adDensityMetricEventSchema = eventSchemaGenerator(
  adDensityMetricSchema,
  "adDensityMetricEvent"
);

export const membershipEventSchema = eventSchemaGenerator(
  membershipSchema,
  "membershipEvent"
);
export const sourcepointConsentCapturedEventSchema = eventSchemaGenerator(
  sourcepointConsentCapturedPayloadSchema,
  "sourcepointConsentCapturedEvent"
);

// TypeScript Types
export type EcommerceEvent = z.infer<typeof ecommerceEventSchema>;
export type HawkEvent = z.infer<typeof hawkEventSchema>;
export type HybridAbTestEvent = z.infer<typeof hybridAbTestEventSchema>;
export type PageLoadEvent = z.infer<typeof pageLoadEventSchema>;
export type PageChangedEvent = z.infer<typeof pageChangedEventSchema>;
export type AuctionPartnerEvent = z.infer<typeof auctionPartnerEventSchema>;
export type UserEvent = z.infer<typeof userEventSchema>;
export type ProductLikedEvent = z.infer<typeof productLikedEventSchema>;
export type ProductUnlikedEvent = z.infer<typeof productUnlikedEventSchema>;
export type CoreWebVitalsThresholdEvent = z.infer<
  typeof coreWebVitalsThresholdEventSchema
>;
export type AdDensityMetricEvent = z.infer<typeof adDensityMetricEventSchema>;
export type MembershipEvent = z.infer<typeof membershipEventSchema>;
export type SourcepointConsentCapturedEvent = z.infer<
  typeof sourcepointConsentCapturedEventSchema
>;

export type EcommerceData = z.infer<typeof ecommerceSchema>;
export type HawkData = z.infer<typeof hawkSchema>;
export type HybridAbTestData = z.infer<typeof hybridAbTestSchema>;
export type PageLoadData = z.infer<typeof pageLoadSchema>;
export type PageChangedData = z.infer<typeof pageChangedSchema>;
export type UserData = z.infer<typeof userSchema>;
export type ProductLikedData = z.infer<typeof productLikedSchema>;
export type ProductUnlikedData = z.infer<typeof productUnlikedSchema>;
export type CoreWebVitalsThresholdData = z.infer<
  typeof coreWebVitalsThresholdSchema
>;
export type AdDensityMetricData = z.infer<typeof adDensityMetricSchema>;
export type MembershipData = z.infer<typeof membershipSchema>;
export type SourcepointConsentCapturedData = z.infer<
  typeof sourcepointConsentCapturedPayloadSchema
>;

export interface GenericEvent {
  name: string;
  payload: Record<string, unknown>;
}

export const isAuctionPartnerEvent = (
  event: GenericEvent
): event is AuctionPartnerEvent => event.name === "auctionPartnerEvent";

export const isPageLoadEvent = (event: GenericEvent): event is PageLoadEvent =>
  event.name === "pageLoadEvent";

export const isHawkEvent = (event: GenericEvent): event is HawkEvent =>
  event.name === "hawkEvent";

export const isUserIdEvent = (event: GenericEvent): event is UserEvent =>
  event.name === "userIdEvent";

export const isPageChangedEvent = (
  event: GenericEvent
): event is PageChangedEvent => event.name === "pageChange";

export const isHybridAbTestEvent = (
  event: GenericEvent
): event is HybridAbTestEvent => event.name === "hybridAbTestEvent";

export const isProductLikedEvent = (
  event: GenericEvent
): event is ProductLikedEvent => event.name === "productLikedEvent";

export const isProductUnlikedEvent = (
  event: GenericEvent
): event is ProductUnlikedEvent => event.name === "productUnlikedEvent";

export const isCoreWebVitalsThresholdEvent = (
  event: GenericEvent
): event is CoreWebVitalsThresholdEvent =>
  event.name === "coreWebVitalsThresholdEvent";

export const isAdDensityMetricEvent = (
  event: GenericEvent
): event is AdDensityMetricEvent => event.name === "adDensityMetricEvent";

export const isMembershipEvent = (
  event: GenericEvent
): event is MembershipEvent => event.name === "membershipEvent";

export const isSourcepointConsentCapturedEvent = (
  event: GenericEvent
): event is SourcepointConsentCapturedEvent =>
  sourcepointConsentCapturedEventSchema.safeParse(event).success;
